import React from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Link as MuiLink,
  Menu,
  MenuItem,
  Stack,
  Typography
} from '@mui/material';
import {
  ArrowBack,
  Map as MapIcon,
  MoreVert,
  OpenInNew,
  People,
  Settings as SettingsIcon,
  Tune
} from '@mui/icons-material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import SubEventForm from './SubEventForm';
import EditButton from 'components/forms/EditButton';
import DeleteButton from 'components/forms/DeleteButton';
import { useRecordContext } from 'components/record/useRecordContext';
import ShowTabs from 'components/tabs/ShowTabs';
import TabElement from 'components/tabs/TabElement';
import SubEventControls from '../Control/SubEventControls';
import Maps from '../Map/Maps';
import Routes from 'components/Map/Routes';
import Competitors from '../Competitor/Competitors';
import UpdateLiveloxUrlDialog from './UpdateLiveloxUrlDialog';

const TAB_ALIASES: Record<string, string> = {
  '0': 'competitors',
  '1': 'maps',
  '2': 'controls'
};

const SubEventPage = () => {
  const navigate = useNavigate();
  const { record: subEvent } = useRecordContext();
  const { tab } = useParams();
  const [menuEl, setMenuEl] = React.useState<null | HTMLElement>(null);
  const [liveloxUrlDialogOpen, setLiveloxUrlDialogOpen] = React.useState(false);

  const event = subEvent.event;
  const activeTab = TAB_ALIASES[tab || ''] || tab || 'competitors';
  const publicUrl = `${import.meta.env.VITE_DOMAIN_EVENTS}${event.slug}/${subEvent.sub}`;
  const replayUrl = `${import.meta.env.VITE_DOMAIN_REPLAY}${event.slug}`;
  const eventUrl = `/my_events/${event.slug}/subevents`;

  const onDeleted = () => {
    navigate(eventUrl);
  };

  return (
    <Box>
      <MuiLink
        component={Link}
        to={eventUrl}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 2, color: 'text.secondary', fontSize: 14 }}
      >
        <ArrowBack fontSize="small" /> Back to event
      </MuiLink>

      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: { xs: 2, md: 3 } }}>
        <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="flex-start">
          <Box
            sx={{
              width: { xs: 44, sm: 56 },
              height: { xs: 44, sm: 56 },
              flexShrink: 0,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <MapIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="h4" component="h1" sx={{ wordBreak: 'break-word' }}>
                {subEvent.name}
              </Typography>
              {subEvent.useGPS && (
                <Chip
                  size="small"
                  label="GPS"
                  sx={{
                    height: 24,
                    fontWeight: 700,
                    fontSize: 11,
                    letterSpacing: '0.02em',
                    textTransform: 'uppercase',
                    color: '#166534',
                    backgroundColor: '#dcfce7',
                    '& .MuiChip-label': { px: 1 }
                  }}
                />
              )}
              {!subEvent.public && (
                <Chip
                  size="small"
                  label="Private"
                  sx={{
                    height: 24,
                    fontWeight: 700,
                    fontSize: 11,
                    letterSpacing: '0.02em',
                    textTransform: 'uppercase',
                    color: '#6b7280',
                    backgroundColor: 'transparent',
                    border: '1px solid #d1d5db',
                    '& .MuiChip-label': { px: 1 }
                  }}
                />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: { xs: 12, sm: 14 } }}>
              {event.name}
              {subEvent.sub ? ` | ${subEvent.sub}` : ''}
              {` | ${subEvent.maps?.length || 0} ${(subEvent.maps?.length || 0) === 1 ? 'map' : 'maps'}`}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ width: { xs: '100%', md: 'auto' } }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<OpenInNew />}
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ flex: { xs: 1, md: 'none' } }}
          >
            Open public page
          </Button>
          <EditButton label="Edit subevent" variant="outlined" size="small" sx={{ flex: { xs: 1, md: 'none' } }}>
            <SubEventForm />
          </EditButton>
          <IconButton onClick={(e) => setMenuEl(e.currentTarget)}>
            <MoreVert />
          </IconButton>
          <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={() => setMenuEl(null)}>
            <MenuItem component="a" href={publicUrl} target="_blank" rel="noopener noreferrer" onClick={() => setMenuEl(null)}>
              Open Live Tracking
            </MenuItem>
            <MenuItem component="a" href={replayUrl} target="_blank" rel="noopener noreferrer" onClick={() => setMenuEl(null)}>
              Open Replay
            </MenuItem>
            {subEvent.liveloxUrl && (
              <MenuItem
                onClick={() => {
                  setMenuEl(null);
                  setLiveloxUrlDialogOpen(true);
                }}
              >
                Update Livelox URL
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                setMenuEl(null);
                navigate(`/sub_events/${subEvent.id}/settings`);
              }}
            >
              Settings
            </MenuItem>
          </Menu>
          {subEvent.liveloxUrl && (
            <UpdateLiveloxUrlDialog open={liveloxUrlDialogOpen} onClose={() => setLiveloxUrlDialogOpen(false)} />
          )}
        </Stack>
      </Stack>

      <ShowTabs baseUrl={`/sub_events/${subEvent.id}`} value={activeTab} defaultValue="competitors">
        <TabElement label="Competitors" value="competitors" icon={<People />}>
          <Competitors />
        </TabElement>
        <TabElement label="Maps and Routes" value="maps" icon={<MapIcon />}>
          <Stack spacing={3}>
            <Maps />
            <Routes />
          </Stack>
        </TabElement>
        <TabElement label="Controls" value="controls" icon={<Tune />}>
          <SubEventControls />
        </TabElement>
        <TabElement label="Settings" value="settings" icon={<SettingsIcon />}>
          <Box sx={{ p: { xs: 1.5, sm: 2.5 }, border: '1px solid', borderColor: 'error.light', borderRadius: 2 }}>
            <Typography variant="subtitle1" color="error" fontWeight={700} sx={{ mb: 1 }}>
              Danger zone
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Destructive actions are kept here, away from everyday subevent operations.
            </Typography>
            <DeleteButton onSuccess={onDeleted} variant="outlined" color="error">
              Are you sure you want to delete this subevent?
            </DeleteButton>
          </Box>
        </TabElement>
      </ShowTabs>
    </Box>
  );
};

export default SubEventPage;
